'use client';

import React, { useState } from 'react';
import { Share2, Check, Copy } from 'lucide-react';

interface ShareButtonProps {
  journeyTitle: string;
  journeyUrl: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export default function ShareButton({ 
  journeyTitle, 
  journeyUrl,
  size = 'md', 
  className = ''
}: ShareButtonProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  // Size configurations
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
  };

  const iconSizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  const handleNativeShare = async () => {
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      try {
        await navigator.share({
          title: journeyTitle,
          text: `Check out this journey: ${journeyTitle}`,
          url: journeyUrl,
        });
        setShowMenu(false);
      } catch (error) {
        // User cancelled or error occurred
        console.log('Share cancelled or failed:', error);
      }
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(journeyUrl);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        setShowMenu(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = journeyUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
          setShowMenu(false);
        }, 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // If native share is available, use it directly on mobile
    if (typeof navigator !== 'undefined' && 'share' in navigator && typeof window !== 'undefined' && window.innerWidth < 768) {
      handleNativeShare();
    } else {
      setShowMenu(!showMenu);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={handleButtonClick}
        className={`
          ${sizeClasses[size]}
          flex 
          items-center 
          justify-center 
          group
          transition-all 
          duration-200
        `}
        aria-label="Share this journey"
        title="Share this journey"
      >
        <Share2
          className={`
            ${iconSizes[size]}
            text-white
            drop-shadow-lg
            group-hover:scale-110
            transition-all 
            duration-200
          `}
          strokeWidth={2}
        />
      </button>

      {/* Share Menu */}
      {showMenu && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setShowMenu(false)}
          />
          
          {/* Menu */}
          <div className="absolute right-0 top-full mt-2 z-50 bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[200px] animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase">Share this journey</p>
            </div>
            
            {/* Native Share (desktop only since mobile uses it directly) */}
            {typeof navigator !== 'undefined' && 'share' in navigator && typeof window !== 'undefined' && window.innerWidth >= 768 && (
              <button
                onClick={handleNativeShare}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3 text-sm"
              >
                <Share2 className="h-4 w-4 text-gray-600" />
                <span className="text-gray-700">Share via...</span>
              </button>
            )}
            
            {/* Copy Link */}
            <button
              onClick={handleCopyLink}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3 text-sm"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 text-green-600" />
                  <span className="text-green-600 font-medium">Link copied!</span>
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 text-gray-600" />
                  <span className="text-gray-700">Copy link</span>
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

